import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  createApiClientApi,
  deleteApiClientApi,
  getApiClientsApi,
  revokeApiClientApi,
} from "../api/apiClientsApi";

const apiClientsKey = (storeId: StoreId | null) => ["apiClients", storeId];

export const useGetApiClientsQuery = (storeId: StoreId | null) =>
  useQuery({
    queryKey: apiClientsKey(storeId),
    queryFn: () => getApiClientsApi(storeId!),
    enabled: !!storeId,
  });

const useApiClientMutation = <T, R>(
  storeId: StoreId | null,
  mutationFn: (storeId: StoreId, variables: T) => Promise<R>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: T) => mutationFn(storeId!, variables),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: apiClientsKey(storeId) }),
  });
};

export const useCreateApiClientMutation = (storeId: StoreId | null) =>
  useApiClientMutation(storeId, createApiClientApi);

export const useRevokeApiClientMutation = (storeId: StoreId | null) =>
  useApiClientMutation(storeId, revokeApiClientApi);

export const useDeleteApiClientMutation = (storeId: StoreId | null) =>
  useApiClientMutation(storeId, deleteApiClientApi);
